import { ProjectQuotationButton } from "@/features/orders/quotation/ProjectQuotationButton";
import { useActiveProject } from "@/features/project/ActiveProject";
import { ListView, ListViewItem, clockIcon, personIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoWorkerName,
  InputField,
  LocalizedDateAndTime,
  ModalSheet,
  SectionContainer,
} from "@repo/ui";
import { useState } from "react";

export type QuotationStep = "OVERVIEW" | "NEW" | "MANAGE";

interface QuotationsModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

export const QuotationsModal = ({
  isOpen,
  closeModal,
}: QuotationsModalProps) => {
  const [step, setInternalStep] = useState<QuotationStep>("OVERVIEW");
  const { opportunity } = useActiveProject();
  const [quotationId, setQuotationId] = useState<string>("");
  const [quotationTitle, setQuotationTitle] = useState<string>("");
  const [oldQuotationId, setOldQuotationId] = useState<string>("");
  const [oldQuotationTitle, setOldQuotationTitle] = useState<string>("");

  const setStep = (newStep: QuotationStep) => {
    if (newStep === "OVERVIEW" || newStep === "NEW") {
      setQuotationId("");
      setOldQuotationId("");
      setQuotationTitle("");
      setOldQuotationTitle("");
    }
    setInternalStep(newStep);
  };

  const quotationInfo = opportunity?.quotations?.quotationInfo ?? [];
  const currentQuotation = quotationInfo.find(
    (quotation) => quotation.id === quotationId
  );

  return (
    <ModalSheet
      title={t("label.quotations")}
      visible={isOpen}
      onClose={closeModal}
      onBack={() => {
        if (step === "OVERVIEW") {
          closeModal();
        } else {
          setStep("OVERVIEW");
        }
      }}
      footer={
        <ProjectQuotationButton
          step={step}
          setStep={setStep}
          quotationId={quotationId}
          quotationTitle={quotationTitle}
          opportunityId={opportunity?.id}
          oldQuotationId={oldQuotationId}
          oldQuotationTitle={oldQuotationTitle}
        />
      }
    >
      {step === "OVERVIEW" && (
        <>
          <ListView id="quotationList">
            {quotationInfo?.map((quotation) => (
              <ListViewItem
                key={quotation.id}
                title={quotation.title}
                description={quotation.id}
                control="navigational"
                onClick={() => {
                  setOldQuotationId(quotation.id);
                  setQuotationId(quotation.id);
                  setQuotationTitle(quotation.title);
                  setOldQuotationTitle(quotation.title);
                  setStep("MANAGE");
                }}
              />
            ))}
          </ListView>
          {!quotationInfo && <div>{t("label.quotations_empty")}</div>}
        </>
      )}
      {(step === "NEW" || step === "MANAGE") && (
        <SectionContainer>
          <InputField
            id="quotationTitle"
            label={t("label.title")}
            value={quotationTitle}
            error={!quotationTitle}
            onChange={(newQuotationTitle) => {
              setQuotationTitle(newQuotationTitle ?? "");
            }}
          />
          <InputField
            id="quotationID"
            label={t("label.id")}
            value={quotationId}
            error={!quotationId}
            onChange={(newQuotationId) => {
              setQuotationId(newQuotationId ?? "");
            }}
          />
          {step === "MANAGE" && quotationInfo && (
            <ListView id="quotationInfo" showDivider={false}>
              <ListViewItem
                leadingIcon={personIcon}
                title={t("label.updated_by")}
                description={
                  <CoWorkerName email={currentQuotation?.createdBy} />
                }
              />
              <ListViewItem
                leadingIcon={clockIcon}
                title={t("label.updated_at")}
                description={
                  currentQuotation?.createdTime ? (
                    <LocalizedDateAndTime date={currentQuotation.createdTime} />
                  ) : (
                    "-"
                  )
                }
              />
            </ListView>
          )}
        </SectionContainer>
      )}
    </ModalSheet>
  );
};
